import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Check, Upload, Camera, FileSpreadsheet, Loader2, Image as ImageIcon } from "lucide-react";
import { api, API, fmtDate } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { A3Board, GudangBoard } from "@/components/A3GudangBoard";
import { SetorBahanBoard } from "@/components/SetorBahanBoard";
import { toast } from "sonner";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const token = () => localStorage.getItem("pas_token");
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const emptyBill = { tanggal_masuk: new Date().toISOString().slice(0, 10), supplier: "", no_tagihan: "", keterangan: "", jumlah: "", deadline: "" };

const Sum = ({ label, value, warna }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <div className={`mt-2 font-display text-2xl font-light ${warna}`}>{value}</div>
  </div>
);

const SupplierBills = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyBill);
  const [f, setF] = useState({ supplier: "", tanggal: "", bulan: "", tahun: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (f.supplier.trim()) params.supplier = f.supplier.trim();
      if (f.tanggal) params.tanggal = f.tanggal;
      if (f.tahun) params.tahun = f.tahun;
      if (f.bulan) params.bulan = f.bulan;
      const { data } = await api.get("/supplier-bills", { params });
      setRows(data);
    } catch {
      toast.error("Gagal memuat tagihan supplier");
    }
  }, [f]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/supplier-bills", { ...form, jumlah: Number(form.jumlah) || 0 });
      toast.success("Tagihan supplier ditambahkan");
      setForm(emptyBill);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah tagihan");
    } finally {
      setBusy(false);
    }
  };

  const bayar = async (r) => {
    try {
      await api.post(`/supplier-bills/${r.id}/pay`);
      toast.success(`Tagihan ${r.nomor} ditandai sudah bayar · silakan unggah bukti`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menandai bayar");
    }
  };

  const hapus = async (r) => {
    if (!window.confirm(`Hapus tagihan ${r.nomor} (${r.supplier})?`)) return;
    try {
      await api.delete(`/supplier-bills/${r.id}`);
      toast.success("Tagihan dihapus");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const uploadBukti = async (r, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/supplier-bills/${r.id}/bukti`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Bukti pembayaran tersimpan");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah bukti");
    }
  };

  const total = rows.reduce((a, r) => a + Number(r.jumlah || 0), 0);
  const belum = rows.filter((r) => !r.paid).reduce((a, r) => a + Number(r.jumlah || 0), 0);

  return (
    <div className="space-y-5" data-testid="supplier-bills">
      <div className="grid gap-4 sm:grid-cols-3">
        <Sum label="Jumlah Tagihan" value={rows.length} warna="text-slate-100" />
        <Sum label="Total Nilai" value={rp(total)} warna="text-slate-100" />
        <Sum label="Belum Dibayar" value={rp(belum)} warna="text-rose-300" />
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-5 md:p-6 space-y-4">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
          Entri Tagihan Supplier
        </h4>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            ["tanggal_masuk", "Tanggal Masuk", "date"],
            ["supplier", "Nama Supplier", "text"],
            ["no_tagihan", "No Tagihan", "text"],
            ["keterangan", "Keterangan", "text"],
            ["jumlah", "Jumlah (Rp)", "text"],
            ["deadline", "Deadline", "date"],
          ].map(([k, label, type]) => (
            <div key={k}>
              <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
              <Input
                data-testid={`supplier-${k}-input`}
                type={type}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 h-11 bg-slate-900/70 border-white/10"
              />
            </div>
          ))}
        </div>
        <Button
          data-testid="supplier-submit-btn"
          type="submit"
          disabled={busy}
          className="h-11 rounded-xl bg-amber-500 text-slate-900 hover:bg-amber-400 px-6 font-semibold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Simpan Tagihan</>}
        </Button>
      </form>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            data-testid="supplier-filter-name"
            placeholder="Filter nama supplier"
            value={f.supplier}
            onChange={(e) => setF({ ...f, supplier: e.target.value })}
            className="h-10 w-52 bg-slate-900/70 border-white/10"
          />
          <Input
            data-testid="supplier-filter-date"
            type="date"
            value={f.tanggal}
            onChange={(e) => setF({ ...f, tanggal: e.target.value })}
            className="h-10 w-auto bg-slate-900/70 border-white/10"
          />
          <select
            data-testid="supplier-filter-month"
            value={f.bulan}
            onChange={(e) => setF({ ...f, bulan: e.target.value })}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
          >
            <option value="">Semua Bulan</option>
            {BULAN.map((b, i) => (
              <option key={b} value={i + 1}>{b}</option>
            ))}
          </select>
          <Input
            data-testid="supplier-filter-year"
            placeholder="Tahun"
            value={f.tahun}
            onChange={(e) => setF({ ...f, tahun: e.target.value })}
            className="h-10 w-24 bg-slate-900/70 border-white/10"
          />
          <Button
            data-testid="supplier-export-btn"
            onClick={() => {
              const q = new URLSearchParams({ auth: token() });
              if (f.supplier.trim()) q.set("supplier", f.supplier.trim());
              if (f.tanggal) q.set("tanggal", f.tanggal);
              if (f.tahun) q.set("tahun", f.tahun);
              if (f.bulan) q.set("bulan", f.bulan);
              window.open(`${API}/supplier-bills/export?${q.toString()}`, "_blank");
            }}
            className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 text-xs font-semibold"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel
          </Button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Nomor", "Tgl Masuk", "Supplier", "No Tagihan", "Keterangan", "Jumlah", "Deadline", "Status", "Bukti", "Aksi"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="supplier-bill-list">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                    Belum ada tagihan supplier pada filter ini.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 font-semibold text-amber-300">{r.nomor}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(r.tanggal_masuk)}</td>
                  <td className="px-3 py-2 text-slate-200">{r.supplier}</td>
                  <td className="px-3 py-2 text-slate-400">{r.no_tagihan || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{r.keterangan || "-"}</td>
                  <td className="px-3 py-2">{rp(r.jumlah)}</td>
                  <td className="px-3 py-2 text-slate-400">{r.deadline ? fmtDate(r.deadline) : "-"}</td>
                  <td className="px-3 py-2">
                    {r.paid ? (
                      <span className="rounded-lg bg-emerald-500/15 px-2 py-1 text-[10px] font-semibold text-emerald-300">
                        SUDAH BAYAR
                      </span>
                    ) : (
                      <Button
                        data-testid={`supplier-pay-${r.nomor}`}
                        onClick={() => bayar(r)}
                        className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 text-[10px] font-semibold uppercase"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Sudah Bayar
                      </Button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.paid ? (
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer rounded-lg border border-white/10 bg-slate-800/70 p-1.5 hover:bg-slate-700">
                          <Upload className="w-3.5 h-3.5" />
                          <input
                            data-testid={`supplier-upload-${r.nomor}`}
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => uploadBukti(r, e.target.files?.[0])}
                          />
                        </label>
                        <label className="cursor-pointer rounded-lg border border-white/10 bg-slate-800/70 p-1.5 hover:bg-slate-700">
                          <Camera className="w-3.5 h-3.5" />
                          <input
                            data-testid={`supplier-camera-${r.nomor}`}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => uploadBukti(r, e.target.files?.[0])}
                          />
                        </label>
                        {r.bukti_file_id && (
                          <a
                            data-testid={`supplier-bukti-${r.nomor}`}
                            href={`${API}/files/${r.bukti_file_id}?auth=${token()}`}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-lg border border-emerald-400/25 bg-emerald-500/10 p-1.5 text-emerald-300"
                          >
                            <ImageIcon className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-600">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`supplier-delete-${r.nomor}`}
                      onClick={() => hapus(r)}
                      className="rounded-lg border border-rose-500/25 bg-rose-500/10 p-1.5 text-rose-300 hover:bg-rose-500/20"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const MarginBahan = () => {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  useEffect(() => {
    api
      .get("/material-margin", {
        params: {
          ...(range.start ? { start: range.start } : {}),
          ...(range.end ? { end: range.end } : {}),
        },
      })
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Gagal memuat margin bahan"));
  }, [range]);
  return (
    <div className="space-y-5" data-testid="margin-bahan">
      <div className="glass rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Dari Tanggal</span>
          <Input
            data-testid="margin-filter-start"
            type="date"
            value={range.start}
            onChange={(e) => setRange({ ...range, start: e.target.value })}
            className="mt-1 h-10 bg-slate-900/70 border-white/10"
          />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Sampai</span>
          <Input
            data-testid="margin-filter-end"
            type="date"
            value={range.end}
            onChange={(e) => setRange({ ...range, end: e.target.value })}
            className="mt-1 h-10 bg-slate-900/70 border-white/10"
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Sum label="Total Margin Terbayar" value={rp(rows.reduce((a, r) => a + r.total_margin, 0))} warna="text-emerald-300" />
        <Sum label="Jenis Bahan" value={rows.length} warna="text-slate-100" />
      </div>
      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
          Margin Bahan (dari tagihan bahan yang sudah dibayar)
        </h4>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Kategori", "Jenis", "Merk", "Ukuran", "Harga Beli", "Harga Jual", "Margin/Unit", "Qty Terbayar", "Total Margin"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="margin-list">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                    Belum ada data margin.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 text-slate-200 font-medium">{r.kategori}</td>
                  <td className="px-3 py-2">{r.jenis}</td>
                  <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                  <td className="px-3 py-2">{rp(r.harga_beli)}</td>
                  <td className="px-3 py-2 text-amber-300">{rp(r.harga_jual)}</td>
                  <td className={`px-3 py-2 font-semibold ${r.margin < 0 ? "text-rose-300" : "text-emerald-300"}`}>
                    {rp(r.margin)}
                  </td>
                  <td className="px-3 py-2 text-slate-400">{r.qty_terbayar}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-300">{rp(r.total_margin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const KumendanBoard = ({ role }) => (
  <Tabs defaultValue="supplier" data-testid="kumendan-board">
    <TabsList className="bg-slate-900/70 border border-white/10 rounded-xl p-1 flex-wrap h-auto" data-testid="kumendan-tabs">
      {[
        ["supplier", "Daftar Tagihan Supplier"],
        ["margin", "Margin Bahan"],
        ["setor", "Setor Bahan Baku"],
        ["a3", "Klik A3+"],
        ["gudang", "Pemasukan & Pengeluaran Gudang"],
      ].map(([v, label]) => (
        <TabsTrigger
          key={v}
          data-testid={`tab-kmd-${v}`}
          value={v}
          className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5"
        >
          {label}
        </TabsTrigger>
      ))}
    </TabsList>
    <TabsContent value="supplier" className="mt-5">
      <SupplierBills />
    </TabsContent>
    <TabsContent value="margin" className="mt-5">
      <MarginBahan />
    </TabsContent>
    <TabsContent value="setor" className="mt-5">
      <SetorBahanBoard />
    </TabsContent>
    <TabsContent value="a3" className="mt-5">
      <A3Board />
    </TabsContent>
    <TabsContent value="gudang" className="mt-5">
      <GudangBoard />
    </TabsContent>
  </Tabs>
);
